import * as EVENTS from '../../common/constant';
import { createElement, isNullOrUndefined as isNOU } from '@syncfusion/ej2-base';
import * as CONSTANT from './../base/constant';
/**
 * ClipBoardCleanup internal component
 *
 * @hidden
 */
export class ClipBoardCleanupAction {
    /**
     * Constructor for creating the component
     *
     * @param {IEditorModel} parent - specifies the parent element
     * @hidden
     * @private
     */
    constructor(parent) {
        this.parent = parent;
        this.addEventListener();
    }
    /* Attaches event listeners for ClopBoard Cleanup operations */
    addEventListener() {
        this.parent.observer.on(EVENTS.INTERNAL_DESTROY, this.destroy, this);
    }
    /* Attaches event listeners for ClopBoard Cleanup operations */
    removeEventListener() {
        this.parent.observer.off(EVENTS.INTERNAL_DESTROY, this.destroy);
    }
    /**
     * Handles the clipboard data processing for cut/copy operations
     *
     * @param {Range} range - The current selection range in the document
     * @param {Element} editableElement - The editable container element
     * @param {string} operation - The clipboard operation type ('cut', 'copy')
     * @returns {ClipboardWriteEventArgs} - An object containing HTML and plain text content for clipboard operations
     */
    handleClipboardProcessing(range, editableElement, operation) {
        this.editableElement = editableElement;
        // Extract HTML and plain text from the selected range
        let { htmlContent, plainTextContent } = this.extractClipboardContentFromSelection(range);
        const isFullBlockSelection = this.isSelectionCoveringSingleBlock(range, htmlContent);
        const isFullLISelected = this.isFullLISelected(range, htmlContent);
        // Wrap list items if selection is within a list
        if (this.parent.domNode.isList(range.commonAncestorContainer)) {
            const formattedClipboardData = this.wrapListStructureForClipboard(range, htmlContent, plainTextContent);
            htmlContent = formattedClipboardData.htmlContent;
            plainTextContent = formattedClipboardData.plainTextContent;
        }
        else if (isFullLISelected) {
            // when whole li is selected
            let closestList = range.commonAncestorContainer;
            // If it's a text node, move to its parent
            if (closestList.nodeName === '#text') {
                closestList = closestList.parentElement;
            }
            // If it's an LI, use it; otherwise, find the closest LI
            const liElement = closestList.nodeName === 'LI' ? closestList : closestList.closest('li');
            // Get the parent list (UL or OL)
            const closestListParent = liElement.parentElement;
            const listContainer = createElement(closestListParent.tagName);
            listContainer.appendChild(createElement('li'));
            listContainer.firstElementChild.innerHTML = htmlContent;
            htmlContent = listContainer.outerHTML;
            const temporaryWrapper = createElement('div');
            temporaryWrapper.innerHTML = htmlContent;
            plainTextContent = this.extractTextFromHtmlNode(temporaryWrapper);
        }
        // Handle cut operation
        if (operation === 'cut') {
            //check if table is in selection
            const tables = this.collectTablesFromSelection(range);
            if (tables.length > 1 || (tables.length === 1 && !this.isSelectionInsideNestedTable(range))) {
                //handle table cut behavior
                this.isTableSelection = true;
                this.clearSelectedTableContent(range);
            }
            else {
                //check if a whole block level element is selected
                this.isTableSelection = false;
                const isSelectionSameBlock = (this.parent.domNode.getImmediateBlockNode(range.startContainer) ===
                    this.parent.domNode.getImmediateBlockNode(range.endContainer));
                //check if media element alone is selected
                const containsMediaContent = this.containsMediaElement(range);
                // Normalize inline elements at the boundaries
                this.cleanEmptyInlineBoundaries(range);
                //to get the nested list range end container
                let nestedListEndContainer;
                let rootParentLIElement;
                if (range.commonAncestorContainer.nodeName === 'LI') {
                    rootParentLIElement = range.commonAncestorContainer;
                    nestedListEndContainer = this.parent.domNode.getImmediateBlockNode(range.endContainer);
                }
                range.deleteContents();
                const sharedContainer = range.commonAncestorContainer;
                if (containsMediaContent) {
                    this.adjustCursorAfterMediaCut(range);
                }
                else if (isFullBlockSelection) {
                    this.fullBlockElementCursorPosition(sharedContainer);
                }
                else if (range.collapsed && sharedContainer.nodeName !== '#text' && !isSelectionSameBlock &&
                    !isNOU(range.startContainer.childNodes[range.startOffset])) {
                    this.restructureContentPostCut(range, rootParentLIElement, nestedListEndContainer);
                }
            }
        }
        return { htmlContent, plainTextContent, operation };
    }
    /* Sets the cursor position after a block-level element is cut*/
    fullBlockElementCursorPosition(blockContainer) {
        const resolvedBlockElement = this.parent.domNode.getImmediateBlockNode(blockContainer);
        const brElement = document.createElement('br');
        if (resolvedBlockElement.childElementCount === 2) {
            const focusElement = this.getFirstBlockElement(resolvedBlockElement);
            focusElement.appendChild(brElement);
            resolvedBlockElement.innerHTML = resolvedBlockElement.firstElementChild.outerHTML;
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, resolvedBlockElement.firstElementChild, 0);
        }
        else {
            resolvedBlockElement.innerHTML = '<br>';
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, resolvedBlockElement, 0);
        }
    }
    /*Adjusts the cursor position after a media element is cut from the document*/
    adjustCursorAfterMediaCut(selectionRange) {
        const startNode = selectionRange.startContainer;
        const isVideoWrapperSpan = startNode.nodeName === 'SPAN' && startNode.className === 'e-video-wrap';
        if (isVideoWrapperSpan) {
            const mediaContainer = startNode;
            const contentContainer = this.parent.domNode.getImmediateBlockNode(mediaContainer);
            let mediaIndex;
            for (let index = 0; index < contentContainer.childNodes.length; index++) {
                if (contentContainer.childNodes[index] === mediaContainer) {
                    mediaIndex = index;
                    break;
                }
            }
            if (mediaIndex === 0) {
                this.setCursorPosition(contentContainer);
            }
            else {
                this.setCursorPosition(contentContainer.childNodes[mediaIndex - 1]);
            }
            contentContainer.removeChild(mediaContainer);
        }
        else {
            const brElement = document.createElement('br');
            if (!isNOU(startNode.childNodes[selectionRange.startOffset]) &&
                startNode.childNodes[selectionRange.startOffset].textContent.trim() === '' &&
                this.isContentContainerEmpty(startNode)) {
                startNode.replaceChild(brElement, startNode.childNodes[selectionRange.startOffset]);
                this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, startNode, startNode.childElementCount);
            }
            else if (this.isContentContainerEmpty(startNode)) {
                startNode.appendChild(brElement);
                this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, startNode, startNode.childElementCount);
            }
            else {
                this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, startNode, selectionRange.startOffset);
            }
        }
    }
    /* Determines whether the li element is fully selected. */
    isFullLISelected(range, htmlContent) {
        let selectedElement;
        if (range.commonAncestorContainer.nodeName === '#text') {
            selectedElement = range.commonAncestorContainer.parentElement;
        }
        else {
            selectedElement = range.commonAncestorContainer;
        }
        if ((selectedElement.nodeName === 'LI' || selectedElement.closest('li'))
            && this.isSelectionCoveringSingleBlock(range, htmlContent)) {
            return true;
        }
        return false;
    }
    /* Wraps list elements (UL/OL) with their parent container if the selection is inside <li> */
    wrapListStructureForClipboard(selectionRange, htmlContent, plainTextContent) {
        const listContainerTag = selectionRange.commonAncestorContainer.nodeName.toLowerCase();
        const listContainer = createElement(listContainerTag);
        listContainer.innerHTML = htmlContent;
        const listItems = Array.from(listContainer.querySelectorAll('li'));
        if (listItems.length > 1) {
            htmlContent = listContainer.outerHTML;
        }
        const temporaryWrapper = createElement('div');
        temporaryWrapper.innerHTML = htmlContent;
        plainTextContent = this.extractTextFromHtmlNode(temporaryWrapper);
        return { htmlContent, plainTextContent };
    }
    /* Extracts both HTML and plain text content from the current selection range. */
    extractClipboardContentFromSelection(range) {
        let htmlContent = '';
        let plainTextContent = '';
        htmlContent = this.getHTMLFromSelectionRange(range);
        const startContainer = range.startContainer.nodeType === Node.TEXT_NODE ?
            range.startContainer.parentElement : range.startContainer;
        const endContainer = range.endContainer.nodeType === Node.TEXT_NODE ?
            range.endContainer.parentElement : range.endContainer;
        const startRootTableContainer = this.locateOutermostTableAncestor(range.startContainer, range);
        const endRootTableContainer = this.locateOutermostTableAncestor(range.endContainer, range);
        const isSelectionNestedTable = startRootTableContainer && endRootTableContainer &&
            startRootTableContainer === endRootTableContainer;
        //handle nested table html content retrival
        if (isSelectionNestedTable && startContainer.closest('table') !== endContainer.closest('table')) {
            const isStartEndBothNestedTable = startContainer.closest('table') !== startRootTableContainer &&
                startRootTableContainer !== endContainer.closest('table');
            const isStartInsideNestedTable = isStartEndBothNestedTable ? false :
                startRootTableContainer !== startContainer.closest('table') ? true : false;
            const nestedTable = isStartEndBothNestedTable ? null : isStartInsideNestedTable ?
                startContainer.closest('table') : endContainer.closest('table');
            htmlContent = this.handleNestedTableHtmlContent(startRootTableContainer, nestedTable, isStartInsideNestedTable, startContainer, endContainer, htmlContent);
        }
        const temporaryContainer = createElement('div');
        temporaryContainer.innerHTML = htmlContent;
        htmlContent = this.normalizeInlineElementWrapping(range, htmlContent);
        // If fails this operation will be handled in wrapListStructureForClipboard method
        if (!(this.parent.domNode.isList(range.commonAncestorContainer) ||
            this.isFullLISelected(range, htmlContent))) {
            plainTextContent = this.extractTextFromHtmlNode(temporaryContainer);
        }
        return { htmlContent, plainTextContent };
    }
    /* Handle the outer html retrival when selection inside nested table */
    handleNestedTableHtmlContent(rootTableContainer, nestedTable, isStartInsideNestedTable, startContainer, endContainer, htmlContent) {
        const tableContainer = createElement('table');
        tableContainer.classList.add('e-rte-table');
        const tableHeadContainer = createElement('thead');
        const tableBodyContainer = createElement('tbody');
        const tableChildElements = Array.from(rootTableContainer.childNodes);
        let rootHeadContainer;
        let rootBodyContainer;
        tableChildElements.forEach((childNode) => {
            if (childNode.nodeName === 'THEAD') {
                rootHeadContainer = childNode;
            }
            else if (childNode.nodeName === 'TBODY') {
                rootBodyContainer = childNode;
            }
        });
        if (rootHeadContainer && rootHeadContainer.contains(startContainer) &&
            rootHeadContainer.contains(endContainer)) {
            //contain inside table header alone
            if (this.isClosestToSameTableCellEle(startContainer, endContainer, nestedTable, isStartInsideNestedTable, 'th')) {
                return htmlContent;
            }
            tableHeadContainer.innerHTML = htmlContent;
            tableContainer.appendChild(tableHeadContainer);
            htmlContent = tableContainer.outerHTML;
        }
        else if (rootBodyContainer && rootBodyContainer.contains(startContainer) &&
            rootBodyContainer.contains(endContainer)) {
            //contain inside table body alone
            if (this.isClosestToSameTableCellEle(startContainer, endContainer, nestedTable, isStartInsideNestedTable, 'td')) {
                return htmlContent;
            }
            tableBodyContainer.innerHTML = htmlContent;
            tableContainer.appendChild(tableBodyContainer);
            htmlContent = tableContainer.outerHTML;
        }
        else if (rootHeadContainer && rootBodyContainer &&
            rootHeadContainer.contains(startContainer) && rootBodyContainer.contains(endContainer)) {
            //contain inside table header and body both
            tableContainer.innerHTML = htmlContent;
            htmlContent = tableContainer.outerHTML;
        }
        return htmlContent;
    }
    /* Check if selection inside same table cell element ('th' or 'td').*/
    isClosestToSameTableCellEle(startContainer, endContainer, nestedTable, isStartInsideNestedTable, tableCellNodeName) {
        if (nestedTable) {
            const processEle = !isStartInsideNestedTable ? startContainer : endContainer;
            return processEle.closest(tableCellNodeName) === nestedTable.parentElement;
        }
        else {
            return startContainer.closest('table').parentElement === endContainer.closest('table').parentElement;
        }
    }
    /* Extracts HTML content and ensures inline elements are properly wrapped if present in the selection.*/
    normalizeInlineElementWrapping(selectionRange, htmlContent) {
        const startNode = selectionRange.startContainer;
        // Check if the selection starts with non-empty text
        if (startNode.textContent.trim() !== '') {
            const ancestorNode = selectionRange.commonAncestorContainer;
            const isTextNode = ancestorNode.nodeName === '#text';
            const isNonBlockNode = !this.parent.domNode.isBlockNode((isTextNode ? ancestorNode.parentNode : ancestorNode));
            if (isNonBlockNode) {
                htmlContent = this.getWrappedAroundInlineElement((isTextNode ? ancestorNode.parentNode : ancestorNode), htmlContent);
            }
        }
        // Special handling for video wrapper elements
        const isVideoWrapper = startNode.nodeName === 'SPAN' &&
            startNode.classList.length > 0 && startNode.classList.contains('e-video-wrap');
        if (isVideoWrapper) {
            htmlContent = startNode.outerHTML;
        }
        return htmlContent;
    }
    /* Extracts HTML content from the current selection range by wrapping it in a temporary container */
    getHTMLFromSelectionRange(selectionRange) {
        const clonedSelection = selectionRange.cloneContents();
        const temporaryContainer = createElement('div');
        temporaryContainer.appendChild(clonedSelection);
        return temporaryContainer.innerHTML;
    }
    /* Returns the outer HTML of the nearest inline-level ancestor after replacing its inner content with the provided HTML content. */
    getWrappedAroundInlineElement(inlineAncestor, contentToWrap) {
        const contentContainer = createElement('div');
        contentContainer.innerHTML = contentToWrap;
        //to retrieve only the wrapper inline element without any inner html in it
        do {
            const clonedInlineAncestor = inlineAncestor.cloneNode(true);
            //swapping the existing html and cloned inline wrapper
            clonedInlineAncestor.innerHTML = contentContainer.innerHTML;
            contentContainer.innerHTML = clonedInlineAncestor.outerHTML;
            inlineAncestor = inlineAncestor.parentElement;
        } while (!isNOU(inlineAncestor) && !this.parent.domNode.isBlockNode(inlineAncestor));
        return contentContainer.innerHTML;
    }
    /* Checks if the current selection range contains a media element (IMG, VIDEO, AUDIO). */
    containsMediaElement(range) {
        const potentialMediaNode = !isNOU(range.commonAncestorContainer.childNodes[range.startOffset]) ?
            (range.commonAncestorContainer.childNodes[range.startOffset]) : null;
        return (!isNOU(potentialMediaNode) &&
            ((potentialMediaNode.nodeName === 'IMG') ||
                (potentialMediaNode.nodeName === 'VIDEO') || (potentialMediaNode.nodeName === 'AUDIO')));
    }
    /* Retrieves all unique table elements within the current selection range. */
    collectTablesFromSelection(selectionRange) {
        const identifiedTables = [];
        // Get the parent table for startContainer
        const startTable = this.locateOutermostTableAncestor(selectionRange.startContainer, selectionRange);
        if (!isNOU(startTable) && identifiedTables.indexOf(startTable) < 0) {
            identifiedTables.push(startTable);
        }
        // Get the parent table for endContainer
        const endTable = this.locateOutermostTableAncestor(selectionRange.endContainer, selectionRange);
        if (!isNOU(endTable) && identifiedTables.indexOf(endTable) < 0) {
            identifiedTables.push(endTable);
        }
        return identifiedTables;
    }
    /* Locates the outermost parent table element that contains the given node. */
    locateOutermostTableAncestor(currentNode, selectionRange) {
        let traversalNode = currentNode;
        let detectedTable = null;
        if (traversalNode === this.editableElement) {
            const offsetIndex = selectionRange.startContainer === traversalNode ?
                selectionRange.startOffset : selectionRange.endOffset;
            const candidateElement = selectionRange.commonAncestorContainer.childNodes[offsetIndex];
            return !isNOU(candidateElement) ? candidateElement.nodeName === 'TABLE' ? candidateElement : null : null;
        }
        else {
            traversalNode = this.parent.domNode.getImmediateBlockNode(traversalNode);
            while (!isNOU(traversalNode.closest('table')) &&
                this.editableElement.contains(traversalNode.closest('table'))) {
                traversalNode = detectedTable = traversalNode.closest('table');
                // to get the root parent element of table
                traversalNode = traversalNode.parentElement;
            }
            return detectedTable;
        }
    }
    /* Determines whether the current selection is within a nested table structure. */
    isSelectionInsideNestedTable(selectionRange) {
        if (selectionRange.startContainer !== this.editableElement && selectionRange.endContainer !== this.editableElement) {
            const startContainer = selectionRange.startContainer.nodeName === '#text' ?
                selectionRange.startContainer.parentElement : selectionRange.startContainer;
            const endContainer = selectionRange.endContainer.nodeName === '#text' ?
                selectionRange.endContainer.parentElement : selectionRange.endContainer;
            return startContainer.closest('table') === endContainer.closest('table');
        }
        return false;
    }
    /* Clears selected content within tables and manages cursor positioning after cut operation. */
    clearSelectedTableContent(selectionRange) {
        const activeSelection = window.getSelection();
        const tablesToClean = [];
        const selectedNodes = Array.from(selectionRange.commonAncestorContainer.childNodes).filter((childNode) => {
            if (childNode.nodeName === 'TABLE') {
                tablesToClean.push(childNode);
            }
            return selectionRange.intersectsNode(childNode);
        });
        // Remove intermediate nodes
        selectedNodes.forEach((node) => {
            this.processSelectedContentWithinRange(node, selectionRange, tablesToClean);
        });
        // Clean up empty tables
        this.removeEmptyTables(tablesToClean);
        // Position cursor
        selectionRange.collapse(true);
        activeSelection.removeAllRanges();
        activeSelection.addRange(selectionRange);
        this.adjustCursorPostTableCut(selectionRange);
    }
    /* Removes intermediateNodes that are fully selected within the given range. */
    removeSelectedNodes(inspectedElement, selectionRange) {
        if (!isNOU(inspectedElement) && selectionRange.intersectsNode(inspectedElement)) {
            const isFullySelected = this.isFullHTMLElementSelected(inspectedElement, selectionRange);
            if (isFullySelected) {
                // Entire element is selected
                inspectedElement.remove();
                return true;
            }
        }
        return false;
    }
    /*  Recursively filters and processes selected content within a range, including table cells and block-level elements. */
    processSelectedContentWithinRange(contentFragment, selectionRange, tablesToClean) {
        if (!isNOU(contentFragment) && selectionRange.intersectsNode(contentFragment)) {
            let isElementRemoved = false;
            if (this.parent.domNode.isBlockNode(contentFragment) &&
                ['TD', 'TH'].indexOf(contentFragment.nodeName) < 0) {
                if (contentFragment.nodeName === 'TR' && isNOU(contentFragment.querySelector('th'))) {
                    isElementRemoved = this.removeSelectedNodes(contentFragment, selectionRange);
                }
                else if (contentFragment.nodeName !== 'TR') {
                    isElementRemoved = this.removeSelectedNodes(contentFragment, selectionRange);
                }
            }
            if (!isElementRemoved) {
                if (contentFragment.nodeName === 'TABLE') {
                    tablesToClean.push(contentFragment);
                    const parentTable = contentFragment;
                    const selectedRows = Array.from(parentTable.querySelectorAll('tr')).filter((rowCandidate) => {
                        return rowCandidate.nodeName === 'TR' && selectionRange.intersectsNode(rowCandidate) &&
                            rowCandidate.closest('table') === parentTable;
                    });
                    selectedRows.forEach((tableRow) => {
                        this.processSelectedContentWithinRange(tableRow, selectionRange, tablesToClean);
                    });
                }
                else if (contentFragment.nodeName === 'TR') {
                    const associatedTable = contentFragment.closest('table');
                    const selectedCells = Array.from(contentFragment.childNodes).filter((cellCandidate) => {
                        return cellCandidate.nodeName !== '#text' && ['TD', 'TH'].indexOf(cellCandidate.nodeName) >= 0 &&
                            selectionRange.intersectsNode(cellCandidate) &&
                            cellCandidate.closest('table') === associatedTable;
                    });
                    selectedCells.forEach((tableCell) => {
                        this.processSelectedContentWithinRange(tableCell, selectionRange, tablesToClean);
                    });
                }
                else if (this.parent.domNode.isBlockNode(contentFragment) &&
                    ['TD', 'TH'].indexOf(contentFragment.nodeName) >= 0 &&
                    this.isFullHTMLElementSelected(contentFragment, selectionRange)) {
                    contentFragment.innerHTML = '';
                }
                else if ((this.parent.domNode.isBlockNode(contentFragment) &&
                    contentFragment.nodeName !== 'TABLE' && this.hasContainsAnyBlockNode(contentFragment)) ||
                    (['TD', 'TH'].indexOf(contentFragment.nodeName) >= 0)) {
                    const selectedChildNodes = Array.from(contentFragment.childNodes);
                    selectedChildNodes.forEach((nestedNode) => {
                        if (nestedNode.nodeName === 'TABLE') {
                            tablesToClean.push(nestedNode);
                            const parentTable = nestedNode;
                            const selectedRows = Array.from(parentTable.querySelectorAll('tr')).filter((rowCandidate) => {
                                return rowCandidate.nodeName === 'TR' && selectionRange.intersectsNode(rowCandidate) &&
                                    rowCandidate.closest('table') === nestedNode;
                            });
                            selectedRows.forEach((tableRow) => {
                                this.processSelectedContentWithinRange(tableRow, selectionRange, tablesToClean);
                            });
                        }
                        else {
                            this.processSelectedContentWithinRange(nestedNode, selectionRange, tablesToClean);
                        }
                    });
                }
                else if (contentFragment.nodeType === Node.ELEMENT_NODE &&
                    isNOU(contentFragment.closest('table')) &&
                    contentFragment !== this.parent.domNode.getImmediateBlockNode(selectionRange.startContainer) &&
                    contentFragment !== this.parent.domNode.getImmediateBlockNode(selectionRange.endContainer)) {
                    const partialRange = selectionRange.cloneRange();
                    partialRange.setEnd(contentFragment, contentFragment.childNodes.length);
                    partialRange.deleteContents();
                }
                else if (contentFragment.nodeType === Node.ELEMENT_NODE) {
                    this.traverseElementTextNodes(contentFragment, selectionRange);
                }
                else {
                    this.clearIntersectingContent(contentFragment, selectionRange);
                }
            }
        }
    }
    /* To retrive the text nodes in element and remove using the help of clearIntersectingContent method */
    traverseElementTextNodes(elementNode, selectionRange) {
        // Traverse inline text nodes within the fragment
        const intersectingTextNodes = [];
        const textNodeIterator = document.createNodeIterator(elementNode, NodeFilter.SHOW_TEXT, {
            acceptNode: (textCandidate) => {
                // Filter out empty or whitespace-only text nodes
                return (selectionRange.intersectsNode(textCandidate) && textCandidate.textContent.trim()) ?
                    NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
            }
        });
        let currentTextNode = textNodeIterator.nextNode();
        while (currentTextNode) {
            intersectingTextNodes.push(currentTextNode);
            currentTextNode = textNodeIterator.nextNode();
        }
        intersectingTextNodes.forEach((textNode) => {
            this.clearIntersectingContent(textNode, selectionRange);
        });
    }
    /* To check and remove the element if full element is selected */
    isFullHTMLElementSelected(blockElement, selectionRange) {
        const elementRange = document.createRange();
        elementRange.selectNode(blockElement);
        return selectionRange.compareBoundaryPoints(Range.START_TO_START, elementRange) <= 0 &&
            selectionRange.compareBoundaryPoints(Range.END_TO_END, elementRange) >= 0;
    }
    /* To check if any block level elements constains inside */
    hasContainsAnyBlockNode(blockElement) {
        return !isNOU(blockElement) && this.parent.domNode.isBlockNode(blockElement) &&
            CONSTANT.BLOCK_TAGS.some((tag) => blockElement.querySelector(tag) !== null);
    }
    /* Iterates through table list and removes any table that is empty. */
    removeEmptyTables(tableElements) {
        for (let tableIndex = tableElements.length - 1; tableIndex >= 0; tableIndex--) {
            this.removeEmptyTableElement(tableElements[tableIndex]);
        }
    }
    /* Sets the cursor position appropriately after a table cut operation within an editable container. */
    adjustCursorPostTableCut(selectionRange) {
        let cursorElement = selectionRange.startContainer;
        if (this.isContentContainerEmpty(this.editableElement) &&
            this.editableElement.childElementCount === 0) {
            if (this.editableElement.innerHTML === '') {
                this.editableElement.innerHTML = '<p><br></p>';
            }
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, this.editableElement.firstChild, 0);
        }
        else if (cursorElement.nodeName === '#text' &&
            cursorElement.textContent === '' && this.isContentContainerEmpty(cursorElement.parentElement)) {
            cursorElement = this.parent.domNode.getImmediateBlockNode(cursorElement);
            cursorElement.appendChild(document.createElement('br'));
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, cursorElement, cursorElement.childElementCount);
        }
        else if (cursorElement.nodeName === '#text' &&
            cursorElement.textContent.length > 0) {
            this.setCursorPosition(cursorElement);
        }
        else if (this.parent.domNode.isBlockNode(cursorElement)) {
            if (cursorElement.nodeName === 'TABLE') {
                this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, cursorElement, 0);
            }
            else {
                this.setCursorPosition(cursorElement);
            }
        }
        else {
            cursorElement = selectionRange.commonAncestorContainer.childNodes[selectionRange.startOffset];
            if (!isNOU(cursorElement)) {
                if (this.isContentContainerEmpty(cursorElement)) {
                    cursorElement.appendChild(document.createElement('br'));
                    this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, cursorElement, cursorElement.childElementCount);
                }
                else {
                    this.setCursorPosition(cursorElement);
                }
            }
        }
    }
    /* Removes a table element if it lacks meaningful content. */
    removeEmptyTableElement(tableElement) {
        if (!isNOU(tableElement.parentElement)) {
            const isContentInsignificant = this.isContentContainerEmpty(tableElement);
            const isElementAttached = tableElement.parentElement.contains(tableElement);
            if (isContentInsignificant && isElementAttached) {
                tableElement.parentElement.removeChild(tableElement);
                return true;
            }
        }
        return false;
    }
    /* Removes content from a given DOM fragment that intersects with the selection range. */
    clearIntersectingContent(contentFragment, selectionRange) {
        if (!isNOU(contentFragment) && selectionRange.intersectsNode(contentFragment)) {
            let updatedTextContent;
            // Handle partial text node selection
            if (contentFragment.nodeName === '#text') {
                updatedTextContent = this.extractUnselectedTextContent(contentFragment, selectionRange);
                contentFragment.textContent = updatedTextContent;
            }
            const EndRangeBlockAncestor = this.parent.domNode.getImmediateBlockNode(selectionRange.endContainer);
            const inlineWrapperParent = this.parent.domNode.getImmediateBlockNode(contentFragment);
            // Cleanup if the updated text content is empty
            if (updatedTextContent === '' &&
                !this.parent.domNode.isBlockNode(contentFragment.parentElement)) {
                let inlineWrapper = contentFragment.parentElement;
                while (!this.parent.domNode.isBlockNode(inlineWrapper.parentElement)) {
                    if (inlineWrapper.parentElement.childNodes.length === 1) {
                        inlineWrapper = inlineWrapper.parentElement;
                    }
                    else {
                        break;
                    }
                }
                this.removeEmptyContentContainer(inlineWrapper);
            }
            if (this.parent.domNode.isBlockNode(inlineWrapperParent) && this.isContentContainerEmpty(inlineWrapperParent) &&
                inlineWrapperParent === EndRangeBlockAncestor &&
                this.isTableSelection && ['TD', 'TH'].indexOf(inlineWrapperParent.nodeName) < 0) {
                if ((!this.editableElement.contains(inlineWrapperParent.closest('table'))) ||
                    (this.editableElement.contains(inlineWrapperParent.closest('table')) &&
                        inlineWrapperParent.childElementCount === 0)) {
                    // remove element if it is not inside table selection
                    this.removeDomElement(inlineWrapperParent, inlineWrapperParent.parentElement);
                }
            }
        }
    }
    /* Removes a parent container from the DOM if it is empty and still attached. */
    removeEmptyContentContainer(containerCandidate) {
        if (!isNOU(containerCandidate) && (['TD', 'TH'].indexOf(containerCandidate.nodeName) < 0) &&
            containerCandidate !== this.editableElement) {
            const isContainerEmpty = this.isContentContainerEmpty(containerCandidate);
            const isContainerAttached = containerCandidate.parentElement.contains(containerCandidate);
            if (isContainerEmpty && isContainerAttached) {
                containerCandidate.parentElement.removeChild(containerCandidate);
            }
        }
    }
    /* Preserves text outside the selection range in a partially selected text node. */
    extractUnselectedTextContent(textFragment, range) {
        const fullText = textFragment.textContent;
        let startIndex = 0;
        let endIndex = fullText.length;
        if (range.startContainer === textFragment) {
            startIndex = range.startOffset;
        }
        if (range.endContainer === textFragment) {
            endIndex = range.endOffset;
        }
        const leadingText = fullText.substring(0, startIndex);
        const trailingText = fullText.substring(endIndex);
        return leadingText + trailingText;
    }
    /* Checks whether a given container is visually and semantically empty. */
    isContentContainerEmpty(containerElement) {
        return !isNOU(containerElement) && containerElement.nodeName !== '#text' &&
            isNOU(containerElement.querySelector('video')) &&
            isNOU(containerElement.querySelector('audio')) &&
            isNOU(containerElement.querySelector('img')) &&
            containerElement.textContent.trim().length === 0;
    }
    /* Check if the element has more than one BR element */
    hasMoreThanOneBRElement(currentEle) {
        const lastChild = !isNOU(currentEle) ? currentEle.lastChild : null;
        const previousSibling = !isNOU(lastChild) ? lastChild.previousSibling : null;
        return lastChild && lastChild.nodeName === 'BR' &&
            (!isNOU(previousSibling) && previousSibling.nodeName === 'BR');
    }
    /* Removes empty inline text nodes at the start and end of a selection range. */
    cleanEmptyInlineBoundaries(selectionRange) {
        const isStartTextNode = selectionRange.startContainer.nodeName === '#text';
        const isEndTextNode = selectionRange.endContainer.nodeName === '#text';
        const startWrapperEle = selectionRange.startContainer.parentElement;
        const endWrapperEle = selectionRange.endContainer.parentElement;
        if (isStartTextNode && !this.parent.domNode.isBlockNode(startWrapperEle) &&
            this.extractUnselectedTextContent(selectionRange.startContainer, selectionRange) === '') {
            this.removeInlineWrapper(startWrapperEle, selectionRange);
        }
        if (isEndTextNode && !this.parent.domNode.isBlockNode(endWrapperEle) &&
            this.extractUnselectedTextContent(selectionRange.endContainer, selectionRange) === '') {
            this.removeInlineWrapper(endWrapperEle, selectionRange);
        }
    }
    /* To remove the inline element wrapper around it */
    removeInlineWrapper(wrapperEle, selectionRange) {
        const partialRange = selectionRange.cloneRange();
        partialRange.setEnd(wrapperEle, wrapperEle.childNodes.length);
        partialRange.deleteContents();
        if (wrapperEle.textContent.trim() === '' && wrapperEle.childElementCount === 0 &&
            !isNOU(wrapperEle.parentElement)) {
            while (!this.parent.domNode.isBlockNode(wrapperEle.parentElement) &&
                this.isContentContainerEmpty(wrapperEle.parentElement) &&
                wrapperEle.parentElement.childElementCount === 1 &&
                wrapperEle.parentElement.firstElementChild === wrapperEle) {
                wrapperEle = wrapperEle.parentElement;
            }
            if (!isNOU(wrapperEle.parentElement) && wrapperEle.parentElement.contains(wrapperEle)) {
                wrapperEle.parentElement.removeChild(wrapperEle);
            }
        }
    }
    /* Updates the DOM structure and cursor position after cutting selected content. */
    restructureContentPostCut(selectionRange, rootParentLIElement, nestedListEndContainer) {
        //range End container Element after cut
        const rangeEndContainer = selectionRange.startContainer.childNodes[selectionRange.startOffset];
        //range Start container Element after cut
        const rangeStartContainer = this.isParentLIEmpty(rootParentLIElement) ?
            rootParentLIElement : rangeEndContainer.previousSibling;
        // check if the start range element is empty
        const emptyElementAtStart = this.getStartRangeEmptyElement(this.parent.domNode.getImmediateBlockNode(rangeStartContainer), selectionRange);
        // check if the end range element is empty
        const emptyElementAtEnd = this.getEndRangeEmptyElement(rangeEndContainer, nestedListEndContainer);
        const brElement = document.createElement('br');
        if (!isNOU(emptyElementAtStart) && !isNOU(emptyElementAtEnd)) {
            // When both start and end range element empty, cursor placement and empty element removal
            if (this.isParentLIEmpty(rootParentLIElement)) {
                // Place cursor at parent LI element
                rootParentLIElement.insertBefore(brElement, rootParentLIElement.firstElementChild);
                this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, rootParentLIElement, 0);
            }
            else {
                emptyElementAtStart.appendChild(brElement);
                // to set the cursor position at the last br element if element has n number of br element inside it
                this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, emptyElementAtStart, emptyElementAtStart.childElementCount);
            }
            this.removeDomElement(emptyElementAtEnd, emptyElementAtEnd.parentElement);
        }
        else if (!isNOU(emptyElementAtEnd) && !isNOU(rangeStartContainer)) {
            // When only end range element empty, cursor placement and empty element removal
            if (selectionRange.commonAncestorContainer.nodeName === 'LI') {
                // Place cursor at parent LI element
                this.setParentLICursorPosition(rangeStartContainer);
            }
            else {
                this.setCursorPosition(this.getDeeptestBlockElement(rangeStartContainer));
            }
            this.removeDomElement(emptyElementAtEnd, emptyElementAtEnd.parentElement);
        }
        else if (selectionRange.commonAncestorContainer.nodeName === 'LI') {
            // when nested cut selection is done
            this.mergeContentWithinSameContainer(rangeStartContainer, rangeEndContainer, nestedListEndContainer);
        }
        else {
            // Across elements cut selection is done
            this.mergeContentAcrossBlocks(rangeStartContainer, rangeEndContainer);
        }
    }
    /* To set cursor position at root parent list in nested selection use case */
    setParentLICursorPosition(rangeStartContainer) {
        const rootParentLIElement = this.parent.domNode.getImmediateBlockNode(rangeStartContainer);
        const brElement = document.createElement('br');
        const nearestBlockElement = rootParentLIElement.querySelector('ul') ||
            rootParentLIElement.querySelector('ol');
        if (rangeStartContainer.nodeName === 'BR' || (rangeStartContainer.nodeName === '#text' &&
            rangeStartContainer.textContent.trim() === '' && !isNOU(rangeStartContainer.previousElementSibling) &&
            rangeStartContainer.previousElementSibling.nodeName === 'BR')) {
            // if parent li has br element as last child
            if (rangeStartContainer.nodeName === 'BR') {
                rootParentLIElement.insertBefore(brElement, nearestBlockElement);
            }
            else {
                rootParentLIElement.replaceChild(brElement, rangeStartContainer);
            }
            let cursorOffset;
            const cursorBRElement = nearestBlockElement.previousElementSibling;
            for (let index = 0; index < rootParentLIElement.childNodes.length; index++) {
                if (rootParentLIElement.childNodes[index] === cursorBRElement) {
                    cursorOffset = index;
                    break;
                }
            }
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, rootParentLIElement, cursorOffset);
        }
        else if (rangeStartContainer === rootParentLIElement) {
            rangeStartContainer.insertBefore(brElement, rangeStartContainer.firstElementChild);
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, rangeStartContainer, 0);
        }
        else {
            this.setCursorPosition(rangeStartContainer);
        }
    }
    /* To get the range Start container empty element */
    getStartRangeEmptyElement(emptyElement, selectionRange) {
        if (!isNOU(emptyElement)) {
            let elementToRemove;
            if (selectionRange.commonAncestorContainer.nodeName === 'LI') {
                if (this.isParentLIEmpty(selectionRange.commonAncestorContainer)) {
                    elementToRemove = selectionRange.commonAncestorContainer;
                }
                else {
                    return null;
                }
            }
            else {
                elementToRemove = this.getDeeptestBlockElement(emptyElement);
                elementToRemove = this.isContentContainerEmpty(elementToRemove) ? elementToRemove : null;
            }
            return elementToRemove;
        }
        else {
            return null;
        }
    }
    /* To get the range end container empty element */
    getEndRangeEmptyElement(emptyElement, nestedListEndContainer) {
        if (!isNOU(emptyElement)) {
            let elementToRemove;
            if (isNOU(nestedListEndContainer)) {
                elementToRemove = this.getFirstBlockElement(emptyElement);
            }
            else {
                elementToRemove = nestedListEndContainer;
            }
            elementToRemove = this.isContentContainerEmpty(elementToRemove) ? elementToRemove : null;
            return elementToRemove;
        }
        else {
            return null;
        }
    }
    /* To get the LI element which has UL as its first child */
    isParentLIEmpty(liElement) {
        if (!isNOU(liElement) && liElement.nodeName === 'LI' && !isNOU(liElement.firstElementChild)) {
            if (this.parent.domNode.isList(liElement.firstElementChild)) {
                const nearestBlockElement = liElement.firstElementChild;
                if (isNOU(nearestBlockElement.previousSibling) || (nearestBlockElement.previousSibling.textContent.trim() === '' &&
                    nearestBlockElement.previousSibling.nodeName === '#text' &&
                    isNOU(nearestBlockElement.previousSibling.previousElementSibling))) {
                    return true;
                }
            }
            return false;
        }
        return false;
    }
    /* Checks if the current selection range fully covers a single block-level element. */
    isSelectionCoveringSingleBlock(selectionRange, htmlContent) {
        let blockContainer;
        if (!this.parent.domNode.isBlockNode(selectionRange.commonAncestorContainer)) {
            blockContainer = this.parent.domNode.getImmediateBlockNode(selectionRange.commonAncestorContainer);
        }
        else {
            blockContainer = selectionRange.commonAncestorContainer;
        }
        return blockContainer.innerHTML.trim() === htmlContent.trim();
    }
    /* Removes a specified DOM element from its container with special handling for <code> elements. */
    removeDomElement(removableElement, containerElement) {
        if (!isNOU(removableElement) && !isNOU(containerElement) && removableElement !== this.editableElement) {
            if (this.hasMoreThanOneBRElement(removableElement)) {
                removableElement.removeChild(removableElement.lastChild);
            }
            else {
                //To get any parent element of container if they are empty
                while (this.isContentContainerEmpty(removableElement.parentElement) &&
                    removableElement.parentElement.childElementCount === 1 &&
                    removableElement.parentElement !== this.editableElement) {
                    removableElement = removableElement.parentElement;
                    containerElement = removableElement.parentElement;
                }
                if (!isNOU(removableElement.nodeName) && removableElement.nodeName === 'CODE' &&
                    removableElement.parentElement.nodeName === 'PRE') {
                    removableElement = containerElement;
                    containerElement = containerElement.parentElement;
                }
                if (containerElement.contains(removableElement)) {
                    containerElement.removeChild(removableElement);
                }
            }
        }
    }
    /* Extracts and removes inline elements from a block container while preserving their content. */
    extractInlineContentFromBlock(blockContainer) {
        const inlineContentWrapper = createElement('div');
        const childNodesList = Array.from(blockContainer.childNodes);
        for (let index = 0; index < childNodesList.length; index++) {
            if (!this.parent.domNode.isBlockNode(childNodesList[index])) {
                if (childNodesList[index].nodeName === 'BR') {
                    // to remove first occuring br tag
                    this.removeDomElement(childNodesList[index], blockContainer);
                    break;
                }
                if (childNodesList[index].nodeName === 'CODE' &&
                    !isNOU(childNodesList[index].parentElement) &&
                    childNodesList[index].parentElement.nodeName === 'PRE') {
                    inlineContentWrapper.innerHTML += childNodesList[index].innerHTML;
                }
                else {
                    inlineContentWrapper.innerHTML += childNodesList[index].nodeName === '#text' ?
                        childNodesList[index].textContent :
                        childNodesList[index].outerHTML;
                }
                this.removeDomElement(childNodesList[index], blockContainer);
            }
        }
        if (this.isContentContainerEmpty(blockContainer)) {
            this.removeDomElement(blockContainer, blockContainer.parentElement);
        }
        blockContainer = inlineContentWrapper;
        return blockContainer;
    }
    /* Returns the inner most block element */
    getDeeptestBlockElement(blockContainer) {
        if (!isNOU(blockContainer) && this.parent.domNode.isBlockNode(blockContainer)) {
            while (!isNOU(blockContainer.lastElementChild) &&
                this.parent.domNode.isBlockNode(blockContainer.lastElementChild)) {
                blockContainer = blockContainer.lastElementChild;
            }
            return blockContainer;
        }
        return null;
    }
    /* Returns the first occuring block element */
    getFirstBlockElement(blockContainer) {
        if (!isNOU(blockContainer) && this.parent.domNode.isBlockNode(blockContainer)) {
            while (!isNOU(blockContainer.firstChild) && !isNOU(blockContainer.firstElementChild)) {
                if ((blockContainer.firstChild.nodeType === Node.TEXT_NODE &&
                    blockContainer.firstChild.textContent.trim().length > 0) ||
                    (blockContainer.firstChild.nodeType !== Node.TEXT_NODE &&
                        !this.parent.domNode.isBlockNode(blockContainer.firstChild))) {
                    break;
                }
                blockContainer = blockContainer.firstElementChild;
            }
            return blockContainer;
        }
        return null;
    }
    /* Merges content between two elements located in different block containers and handles cleanup. */
    mergeContentAcrossBlocks(startBlockElement, endBlockElement) {
        // Traverse to the deepest inline element within the start block
        if (this.parent.domNode.isBlockNode(startBlockElement)) {
            startBlockElement = this.getDeeptestBlockElement(startBlockElement);
        }
        // Traverse to the shallowest inline element within the end block
        if (this.parent.domNode.isBlockNode(endBlockElement)) {
            endBlockElement = this.getFirstBlockElement(endBlockElement);
        }
        // Extract inline content from the end block if needed
        if (this.parent.domNode.isBlockNode(endBlockElement)) {
            endBlockElement = this.extractInlineContentFromBlock(endBlockElement);
        }
        if (!isNOU(startBlockElement)) {
            if (!isNOU(startBlockElement.lastChild)) {
                // Set cursor to the last child of the start block
                this.setCursorPosition(startBlockElement.lastChild);
            }
            else {
                this.setCursorPosition(startBlockElement);
            }
            // Merge content
            startBlockElement.insertAdjacentHTML('beforeend', endBlockElement.innerHTML);
        }
    }
    /* Merges content between two elements that share the same parent container and performs cleanup. */
    mergeContentWithinSameContainer(startLiElement, endLiElement, nestedListEndContainer) {
        const canMergeListElement = startLiElement.nodeName !== 'LI' && (startLiElement.textContent.trim() !== '' ||
            (startLiElement.nodeName === '#text' && !isNOU(startLiElement.previousElementSibling) &&
                startLiElement.previousElementSibling.nodeName !== 'BR'));
        if (!isNOU(nestedListEndContainer) && this.isParentLIEmpty(nestedListEndContainer)) {
            this.setParentLICursorPosition(startLiElement);
        }
        else if (canMergeListElement) {
            // Traverse to the shallowest inline element within the end block
            if (this.parent.domNode.isBlockNode(endLiElement)) {
                endLiElement = this.getFirstBlockElement(endLiElement);
            }
            // Extract inline content if end element is still a block
            if (this.parent.domNode.isBlockNode(endLiElement)) {
                endLiElement = this.extractInlineContentFromBlock(endLiElement);
            }
            // Set cursor position at the start element
            this.setCursorPosition(startLiElement);
            const sharedContainer = startLiElement.parentElement;
            const contentFragment = document.createDocumentFragment();
            // Move all children from end element into the fragment
            while (!isNOU(endLiElement.firstChild)) {
                contentFragment.appendChild(endLiElement.firstChild);
            }
            // Insert the fragment after the start element
            sharedContainer.insertBefore(contentFragment, startLiElement.nextSibling);
        }
    }
    /* Sets the cursor position at the end of the specified element or its content. */
    setCursorPosition(focusTarget) {
        let cursorOffset;
        if (!isNOU(focusTarget)) {
            if (focusTarget.nodeName === '#text') {
                if (focusTarget.textContent.length > 0) {
                    cursorOffset = focusTarget.textContent.length;
                }
                else {
                    const focusChildNodes = Array.from(focusTarget.parentElement.childNodes);
                    for (let index = 0; index < focusChildNodes.length; index++) {
                        if (focusChildNodes[index] === focusTarget) {
                            cursorOffset = index;
                            break;
                        }
                    }
                    focusTarget = focusTarget.parentElement;
                }
            }
            else {
                // Handle media or special wrappers
                if (focusTarget.nodeName !== '#text' && !isNOU(focusTarget.childNodes) &&
                    (focusTarget.nodeName === 'IMG' || focusTarget.classList.contains('e-audio-wrap') ||
                        focusTarget.classList.contains('e-video-wrap'))) {
                    cursorOffset = 1;
                }
                else {
                    if (this.parent.domNode.isBlockNode(focusTarget) && !isNOU(focusTarget.lastChild) &&
                        ((focusTarget.lastChild.nodeName === 'BR') || (focusTarget.lastChild.textContent.trim() === '' &&
                            !isNOU(focusTarget.lastChild.previousElementSibling) &&
                            focusTarget.lastChild.previousElementSibling.nodeName === 'BR'))) {
                        // if element has br tag as last element child
                        focusTarget.appendChild(document.createElement('br'));
                        for (let index = 0; index < focusTarget.childNodes.length; index++) {
                            if (focusTarget.childNodes[index].nodeName === 'BR') {
                                cursorOffset = index;
                            }
                        }
                    }
                    else {
                        // Traverse to the deepest valid child
                        while (!isNOU(focusTarget.lastChild)) {
                            focusTarget = focusTarget.lastChild;
                        }
                        cursorOffset = focusTarget.textContent.length;
                    }
                }
            }
            this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, focusTarget, cursorOffset);
        }
    }
    /* Recursively extracts plain text content from an HTML node hierarchy. */
    extractTextFromHtmlNode(sourceNode) {
        let plainText = '';
        for (const childNode of Array.from(sourceNode.childNodes)) {
            if (childNode.nodeType === Node.TEXT_NODE) {
                plainText += childNode.nodeValue;
            }
            else if (childNode.nodeType === Node.ELEMENT_NODE) {
                const tagName = childNode.nodeName.toLowerCase();
                switch (tagName) {
                    case 'br':
                        plainText += '\r\n';
                        break;
                    case 'p':
                    case 'div':
                    case 'h1':
                    case 'h2':
                    case 'h3':
                    case 'h4':
                    case 'h5':
                    case 'h6':
                    case 'blockquote':
                    case 'li':
                    case 'pre':
                        plainText += this.extractTextFromHtmlNode(childNode) + '\r\n';
                        break;
                    case 'table':
                        if (tagName === 'table' && sourceNode.nodeName.toLowerCase() === 'td') {
                            plainText += this.serializeTableToPlainText(childNode);
                            continue;
                        }
                        else {
                            plainText += this.serializeTableToPlainText(childNode) + '\r\n';
                        }
                        break;
                    case 'img':
                    case 'audio':
                    case 'video': {
                        plainText += '';
                        break;
                    }
                    default:
                        plainText += this.extractTextFromHtmlNode(childNode);
                }
            }
        }
        return plainText;
    }
    /* Converts an HTML table element into a plain text format using tabs and newlines. */
    serializeTableToPlainText(tableElement) {
        let plainText = '';
        const tableRows = Array.from(tableElement.querySelectorAll('tr')).filter((row) => {
            return (row.closest('table') === tableElement);
        });
        for (let index = 0; index < tableRows.length; index++) {
            const tableCells = Array.from(tableRows[index].querySelectorAll('th, td')).filter((cell) => {
                return (cell.closest('tr') === tableRows[index]);
            });
            let rowContent = '';
            for (const cellNode of tableCells) {
                const cellText = this.extractTextFromHtmlNode(cellNode);
                rowContent += cellText.includes('\r\n') ? cellText : (cellText + '\t');
            }
            plainText += rowContent + '\r\n';
        }
        return plainText;
    }
    /**
     * Cleans up resources when the component is destroyed
     *
     * @returns {void} - No return value
     * @public
     */
    destroy() {
        this.removeEventListener();
    }
}
